import {
  Directive,
  effect,
  EffectRef,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  OnInit,
  Renderer2,
  untracked,
} from '@angular/core';

@Directive({ selector: '[appWmIcon]' })
export class WmIconDirective implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private injector = inject(Injector);

  inputParameter = model<string>();
  fontAwesomeStyle = model<string>(); // default--> far
  additionalClasses = input<any>();
  size = input<any>();
  transformation = input<any>();

  /*ICON PARAMETERS*/
  positionsSets = {
    /*for Icons*/
    top_right: 'shrink-5 up-6 right-14',
    top_right_extraspace: 'shrink-5 up-6 right-16',
    bottom_right: 'shrink-5 down-1 right-12',
    /*for text*/
    text_center_right: 'shrink-6 right-12',
    text_center_right_extraspace: 'shrink-6 right-17',
  };

  definedParameters = {
    tt: { mainIcon: 'fa-thermometer-half' },
    hcl: { mainIcon: 'fa-cloud-fog' },
    tt_F: { mainIcon: 'fa-thermometer-half' },
    tx: { mainIcon: 'fa-thermometer-full' },
    tx_F: { mainIcon: 'fa-thermometer-full' },
    tn: { mainIcon: 'fa-thermometer-empty' },
    tn_F: { mainIcon: 'fa-thermometer-empty' },
    gmaAlarm: { mainIcon: 'fa-exclamation-triangle' },
    td: { mainIcon: 'fa-dewpoint' },
    humidityIndex: {
      type: 'index',
      mainIcon: 'fa-humidity',
      brackets: 'fa-square',
    },
    inversionIndex: {
      type: 'index',
      mainIcon: 'fa-layer-group',
      brackets: 'fa-square',
    },
    td_F: { mainIcon: 'fa-dewpoint' },
    n: { mainIcon: 'fa-cloud' },
    ne: { mainIcon: 'fa-cloud' },
    ne_symbol: { mainIcon: 'fa-cloud' },
    nl: { mainIcon: 'fa-cloud-download-alt' },
    nl_symbol: { mainIcon: 'fa-cloud-arrow-down' },
    nlow: { mainIcon: 'fa-cloud-download-alt' },
    nm: { mainIcon: 'fa-cloud-minus' },
    nh: { mainIcon: 'fa-cloud-upload-alt' },
    nm_symbol: { mainIcon: 'fa-cloud-minus' },
    nh_symbol: { mainIcon: 'fa-cloud-arrow-up' },
    ss1: { mainIcon: 'fa-sunglasses' },
    ff: { mainIcon: 'fa-wind' },
    ff_mph: { mainIcon: 'fa-wind' },
    ff_ms: { mainIcon: 'fa-wind' },
    ff_kn: { mainIcon: 'fa-wind' },
    wind: { mainIcon: 'fa-wind' },
    fg: { mainIcon: 'fa-windsock' },
    ffg: { mainIcon: 'fa-windsock' },
    fx: { mainIcon: 'fa-windsock' },
    fx_mph: { mainIcon: 'fa-windsock' },
    ffx: { mainIcon: 'fa-windsock' },
    ffx_mph: { mainIcon: 'fa-windsock' },
    ffx_ms: { mainIcon: 'fa-windsock' },
    ffx_kn: { mainIcon: 'fa-windsock' },
    dd: { mainIcon: 'fa-location-arrow' },
    dd_degree: { mainIcon: 'fa-compass' },
    ddg: {
      mainIcon: 'fa-location-arrow',
      additionalIcon: 'fa-windsock',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    wave_height: {
      mainIcon: 'fa-water',
      additionalIcon: 'fa-arrows-up-down',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    wave_dir: {
      mainIcon: 'fa-water',
      additionalIcon: 'fa-location-arrow',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    wave_duration: {
      mainIcon: 'fa-water',
      additionalIcon: 'fa-timer',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    total_wave_hs: {
      mainIcon: 'fa-wave',
      additionalIcon: 'fa-arrows-up-down',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    total_wave_hmax: {
      mainIcon: 'fa-wave',
      additionalIcon: 'fa-arrow-up-to-line',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    total_wave_dir: {
      mainIcon: 'fa-wave',
      additionalIcon: 'fa-location-arrow',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    total_wave_tp: {
      mainIcon: 'fa-wave',
      additionalIcon: 'fa-hourglass-start',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    total_wave_ap: {
      mainIcon: 'fa-wave',
      additionalIcon: 'fa-hourglass-half',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    swell_height: {
      mainIcon: 'fa-water-arrow-down',
      additionalIcon: 'fa-arrows-up-down',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    swell_dir: {
      mainIcon: 'fa-water-arrow-down',
      additionalIcon: 'fa-location-arrow',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    swell_duration: {
      mainIcon: 'fa-water-arrow-down',
      additionalIcon: 'fa-timer',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    cloud_base: {
      mainIcon: 'fa-arrow-up-to-line',
      additionalIcon: 'fa-cloud',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    water_temp: {
      mainIcon: 'fa-water',
      additionalIcon: 'fa-temperature-quarter',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    webcam: { mainIcon: 'fa-video' },
    salt: { mainIcon: 'fa-mountain' },
    salt2: { mainIcon: 'fa-mountain' },
    vis: { mainIcon: 'fa-binoculars' },
    vv: { mainIcon: 'fa-binoculars' },
    road_tt: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt2: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt3: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt4: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt5: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt6: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt7: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt8: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt9: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt10: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt11: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_tt12: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    tb0: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition2: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition3: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition4: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition5: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition6: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition7: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition8: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition9: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition10: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition11: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_condition12: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_rsc: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    road_rst: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_rst_F: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_rst_max: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_rst_max_F: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    road_rst_min: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    road_rst_min_F: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    bike_rsc: { mainIcon: 'fa-biking' },
    bike_rst: {
      mainIcon: 'fa-biking',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bike_rst_F: {
      mainIcon: 'fa-biking',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bike_rst_max: {
      mainIcon: 'fa-biking',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bike_rst_max_F: {
      mainIcon: 'fa-biking',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bike_rst_min: {
      mainIcon: 'fa-biking',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    bike_rst_min_F: {
      mainIcon: 'fa-biking',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    foot_rsc: { mainIcon: 'fa-walking' },
    foot_rst: {
      mainIcon: 'fa-walking',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    foot_rst_F: {
      mainIcon: 'fa-walking',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    foot_rst_max: {
      mainIcon: 'fa-walking',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    foot_rst_max_F: {
      mainIcon: 'fa-walking',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    foot_rst_min: {
      mainIcon: 'fa-walking',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    foot_rst_min_F: {
      mainIcon: 'fa-walking',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    bridge_rsc: { mainIcon: 'fa-archway' },
    bridge_rst: {
      mainIcon: 'fa-archway',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bridge_rst_F: {
      mainIcon: 'fa-archway',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bridge_rst_max: {
      mainIcon: 'fa-archway',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bridge_rst_max_F: {
      mainIcon: 'fa-archway',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    bridge_rst_min: {
      mainIcon: 'fa-archway',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    bridge_rst_min_F: {
      mainIcon: 'fa-archway',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['bottom_right'],
    },
    road_rh: {
      mainIcon: 'fa-wm-road',
      mainStyle: 'fak',
      additionalIcon: 'fa-humidity',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    // without minus
    t5: {
      mainIcon: 'fa-thermometer-half',
      text: '5',
      text_position: this.positionsSets['text_center_right'],
    },
    tg0: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '0',
      text_position: this.positionsSets['text_center_right'],
    },
    tg5: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '5',
      text_position: this.positionsSets['text_center_right'],
    },
    tg5_2: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '5',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_5cm: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '5',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_5cm_F: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '2',
      text_position: this.positionsSets['text_center_right'],
    },
    tg10: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '10',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_10cm: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '10',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_10cm_F: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '4',
      text_position: this.positionsSets['text_center_right'],
    },
    tg20: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '20',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_20cm: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '20',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_20cm_F: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '8',
      text_position: this.positionsSets['text_center_right'],
    },
    tg30: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '30',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_30cm: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '30',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_30cm_F: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '30',
      text_position: this.positionsSets['text_center_right'],
    },
    tg50: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '50',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_50cm: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '50',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_50cm_F: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '20',
      text_position: this.positionsSets['text_center_right'],
    },
    tg100: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '100',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_100cm: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '100',
      text_position: this.positionsSets['text_center_right'],
    },
    tg_100cm_F: {
      mainIcon: 'fa-thermometer-half',
      additionalIcon: 'fa-minus',
      additionalIcon_position: this.positionsSets['top_right'],
      text: '40',
      text_position: this.positionsSets['text_center_right'],
    },
    wft: { mainIcon: 'fa-water' },
    ww: { mainIcon: 'fa-umbrella' },
    r_type: { mainIcon: 'fa-umbrella' },
    r_intensity: { mainIcon: 'fa-raindrops' },
    r_avail: { mainIcon: 'fa-tint' },
    rh: { mainIcon: 'fa-humidity' },
    cond: { mainIcon: 'fa-plug-circle-bolt' },
    road_grip: { mainIcon: 'fa-tire' },
    freezing_tt: { mainIcon: 'fa-temperature-frigid' },
    snow_height: {
      mainIcon: 'fa-snowflake',
      additionalIcon: 'fa-ruler-vertical',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    sn: {
      mainIcon: 'fa-snowflake',
      additionalIcon: 'fa-ruler-vertical',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    sn_fresh: {
      mainIcon: 'fa-snowflake',
      additionalIcon: 'fa-plus',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    sn_panel: {
      mainIcon: 'fa-snowflake',
      additionalIcon: 'fa-solar-panel',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    sn_in: {
      mainIcon: 'fa-snowflake',
      additionalIcon: 'fa-ruler-vertical',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    ppp: {
      mainIcon: 'fa-stopwatch',
      text: 'NN',
      text_position: this.positionsSets['top_right_extraspace'],
    },
    qfe: {
      mainIcon: 'fa-stopwatch',
      text: 'Stat',
      text_position: this.positionsSets['top_right_extraspace'],
    },
    rr: { mainIcon: 'fa-flask' },
    rr_in: { mainIcon: 'fa-flask' },
    warn: { mainIcon: 'fa-exclamation-triangle' },
    rst: { mainIcon: 'fa-thermometer-half' },
    rsc: { mainIcon: 'fa-wm-road', mainStyle: 'fak' },
    igc: { mainIcon: 'fa-sprinkler' },
    igp: { mainIcon: 'fa-sprinkler' },
    wwf: {
      mainIcon: 'fa-cloud-fog',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wwd: {
      mainIcon: 'fa-cloud-fog',
      additionalIcon: 'fa-percent',
      mainStyle: 'fas',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wws: {
      mainIcon: 'fa-cloud-hail',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wwp: {
      mainIcon: 'fa-cloud-rain',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wwc: {
      mainIcon: 'fa-cloud-showers',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wwt: {
      mainIcon: 'fa-bolt',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    ww_blade_ice: {
      mainIcon: 'fa-shield',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    pwwhw: {
      mainIcon: 'fa-shield-slash',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    pwwsp: {
      mainIcon: 'fa-snowflake',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    pwdust: {
      mainIcon: 'fa-sun-dust',
      additionalIcon: 'fa-percent',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    tt_950hp: {
      mainIcon: 'fa-thermometer-half',
      text: '950',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    tt_950hp_F: {
      mainIcon: 'fa-thermometer-half',
      text: '950',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    dd_120: {
      mainIcon: 'fa-location-arrow',
      text: '120',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    dd_950: {
      mainIcon: 'fa-location-arrow',
      text: '950',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff_120: {
      mainIcon: 'fa-wind',
      text: '120',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff_950: {
      mainIcon: 'fa-wind',
      text: '950',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff_950_mph: {
      mainIcon: 'fa-wind',
      text: '950',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff50_ms: {
      mainIcon: 'fa-wind',
      text: '50',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff50_kn: {
      mainIcon: 'fa-wind',
      text: '50',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff100_ms: {
      mainIcon: 'fa-wind',
      text: '100',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ff100_kn: {
      mainIcon: 'fa-wind',
      text: '100',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ffx50_kn: {
      mainIcon: 'fa-windsock',
      text: '50',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ffx50_ms: {
      mainIcon: 'fa-windsock',
      text: '50',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ffx100_ms: {
      mainIcon: 'fa-windsock',
      text: '100',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    ffx100_kn: {
      mainIcon: 'fa-windsock',
      text: '100',
      text_position: this.positionsSets['text_center_right_extraspace'],
    },
    snlv: { mainIcon: 'fa-mountains' },
    snlv_ft: { mainIcon: 'fa-mountains' },
    symbol: { mainIcon: 'fa-clouds-sun' },
    i_gm: {
      type: 'index',
      mainIcon: 'fa-flower-daffodil',
      brackets: 'fa-square',
    },
    i_he: {
      type: 'index',
      mainIcon: 'fa-temperature-hot',
      brackets: 'fa-square',
    },
    i_co: {
      type: 'index',
      mainIcon: 'fa-temperature-frigid',
      brackets: 'fa-square',
    },
    i_hr: {
      type: 'index',
      mainIcon: 'fa-cloud-showers-heavy',
      brackets: 'fa-square',
    },
    i_lr: {
      type: 'index',
      mainIcon: 'fa-cloud-showers',
      brackets: 'fa-square',
    },
    i_st: {
      type: 'index',
      mainIcon: 'fa-windsock',
      brackets: 'fa-square',
    },
    i_th: {
      type: 'index',
      mainIcon: 'fa-bolt',
      brackets: 'fa-square',
    },
    i_the: {
      type: 'index',
      mainIcon: 'fa-bolt',
      brackets: 'fa-square',
    },
    i_uv: {
      type: 'index',
      mainIcon: 'fa-sun',
      brackets: 'fa-square',
    },
    i_ld: {
      type: 'index',
      mainIcon: 'fa-leaf',
      brackets: 'fa-square',
    },
    i_sl: {
      type: 'index',
      mainIcon: 'fa-snowflake',
      brackets: 'fa-square',
    },
    i_di: {
      type: 'index',
      mainIcon: 'fa-plane',
      brackets: 'fa-square',
    },
    wing_rst: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wing_rst_F: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wing_rst_max: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wing_rst_max_F: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wing_rst_min: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wing_rst_min_F: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    wing_rsc: {
      mainIcon: 'fa-plane',
      additionalIcon: 'fa-cloud-sleet',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    i_sl_wing: {
      type: 'index',
      mainIcon: 'fa-snowflake',
      brackets: 'fa-square',
    },
    i_sl2: {
      mainIcon: 'fa-train',
      mainTransform: 'rotate-30',
      additionalIcon: 'fa-exclamation-triangle',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    rail_tt: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    rail_tt_F: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    rail_tn: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    rail_tn_F: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    rail_tx: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    rail_tx_F: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    rail_condition: {
      mainIcon: 'fa-train',
      additionalIcon: 'fa-cloud-sleet',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_tt: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_tt_F: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_tn: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_tn_F: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-thermometer-empty',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_tx: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_tx_F: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-thermometer-full',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    overhead_condition: {
      mainIcon: 'fa-broadcast-tower',
      additionalIcon: 'fa-cloud-sleet',
      additionalIcon_position: this.positionsSets['top_right_extraspace'],
    },
    i_pi: {
      type: 'index',
      mainIcon: 'fa-dove',
      brackets: 'fa-square',
    },
    i_ff_pi: {
      type: 'index',
      mainIcon: 'fa-wind',
      brackets: 'fa-square',
    },
    i_hfo_pi: {
      type: 'index',
      mainIcon: 'fa-grip-lines',
      brackets: 'fa-square',
    },
    i_ne_pi: {
      type: 'index',
      mainIcon: 'fa-clouds',
      brackets: 'fa-square',
    },
    i_nl_pi: {
      type: 'index',
      mainIcon: 'fa-cloud-download-alt',
      brackets: 'fa-square',
    },
    i_nm_pi: {
      type: 'index',
      mainIcon: 'fa-cloud-minus',
      brackets: 'fa-square',
    },
    i_nh_pi: {
      type: 'index',
      mainIcon: 'fa-cloud-upload-alt',
      brackets: 'fa-square',
    },
    i_rr_pi: {
      type: 'index',
      mainIcon: 'fa-cloud-rain',
      brackets: 'fa-square',
    },
    i_th_pi: {
      type: 'index',
      mainIcon: 'fa-cloud-bolt',
      brackets: 'fa-square',
    },
    i_iv_pi: {
      type: 'index',
      mainIcon: 'fa-layer-group',
      brackets: 'fa-square',
    },
    i_co_pi: {
      type: 'index',
      mainIcon: 'fa-temperature-frigid',
      brackets: 'fa-square',
    },
    i_he_pi: {
      type: 'index',
      mainIcon: 'fa-temperature-hot',
      brackets: 'fa-square',
    },
    i_fo_pi: {
      type: 'index',
      mainIcon: 'fa-binoculars',
      brackets: 'fa-square',
    },
    i_hu_pi: {
      type: 'index',
      mainIcon: 'fa-humidity',
      brackets: 'fa-square',
    },
    i_gp: {
      type: 'index',
      mainIcon: 'fa-sprinkler',
      brackets: 'fa-square',
    },
    i_gc: {
      type: 'index',
      mainIcon: 'fa-sprinkler',
      brackets: 'fa-square',
    },
    i_ein: {
      type: 'index',
      mainIcon: 'fa-truck-plow',
      brackets: 'fa-square',
    },
    i_isn: {
      type: 'index',
      mainIcon: 'fa-industry',
      mainStyle: 'fak',
      brackets: 'fa-square',
    },
    i_blade_ice: {
      type: 'index',
      mainIcon: 'fa-shield-exclamation',
      brackets: 'fa-square',
    },
    i_sp_snow: {
      type: 'index',
      mainIcon: 'fa-snowflake',
      brackets: 'fa-square',
    },
    i_fg: {
      type: 'index',
      mainIcon: 'fa-cloud-fog',
      brackets: 'fa-square',
    },
    i_dust: {
      type: 'index',
      mainIcon: 'fa-sun-dust',
      brackets: 'fa-square',
    },
    i_hw: {
      type: 'index',
      mainIcon: 'fa-shield-slash',
      brackets: 'fa-square',
    },
    dd_hub: {
      mainIcon: 'fa-wind-turbine',
      additionalIcon: 'fa-location-arrow',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    ff_hub: {
      mainIcon: 'fa-wind-turbine',
      additionalIcon: 'fa-wind',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    ffx_hub: {
      mainIcon: 'fa-wind-turbine',
      additionalIcon: 'fa-windsock',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    windpower: {
      mainIcon: 'fa-wind-turbine',
    },
    windpower_bil: {
      mainIcon: 'fa-wind-turbine',
      additionalIcon: 'fa-shield-minus',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    windpower_bic: {
      mainIcon: 'fa-wind-turbine',
      additionalIcon: 'fa-shield-check',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    solarpower: {
      mainIcon: 'fa-sun-bright',
    },
    solarpower_snl: {
      mainIcon: 'fa-sun-bright',
      additionalIcon: 'fa-shield-minus',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    solarpower_snc: {
      mainIcon: 'fa-sun-bright',
      additionalIcon: 'fa-shield-check',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    blade_rst: {
      mainIcon: 'fa-shield',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    blade_rsc: {
      mainIcon: 'fa-shield',
    },
    panel_rst: {
      mainIcon: 'fa-solar-panel',
      additionalIcon: 'fa-thermometer-half',
      additionalIcon_position: this.positionsSets['top_right'],
    },
    panel_rsc: {
      mainIcon: 'fa-solar-panel',
    },
  };

  private renderEffect?: EffectRef;
  private hostContainer: any | null = null;

  ngOnInit(): void {
    this.renderEffect = effect(
      () => {
        this.inputParameter();
        this.fontAwesomeStyle();
        this.additionalClasses();
        this.size();
        this.transformation();

        untracked(() => {
          this.cleanupPreviouslyRendered();
          this.fontAwesomeStyle.set(this.checkFontAwesomeStyle());
          this.checkHowToBuildIcon();
        });
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    this.renderEffect?.destroy();
    this.cleanupPreviouslyRendered();
  }

  private cleanupPreviouslyRendered(): void {
    const parentNode = this.renderer.parentNode(this.elementRef.nativeElement);

    const childElements = parentNode?.children ?? [];

    try {
      for (let child of childElements) {
        this.renderer.removeChild(parentNode, child);
      }
    } catch {}

    try {
      this.renderer.removeAttribute(
        this.elementRef.nativeElement,
        'data-fa-transform'
      );
    } catch {}
  }

  checkFontAwesomeStyle(): string {
    const fontAwesomeStyle = this.fontAwesomeStyle();
    return typeof fontAwesomeStyle === 'undefined' ? 'far' : fontAwesomeStyle;
  }

  /*takes the parameter given to the directive (inputParameter) and checks if it is in definedParameters or if it is a specialParameter*/
  checkIfIconIsDefined(): boolean {
    const inputParameter = this.inputParameter();
    if (this.definedParameters.hasOwnProperty(inputParameter)) {
      return true;
    }
    if (
      !this.definedParameters.hasOwnProperty(inputParameter) &&
      this.checkForSpecialParameter()
    ) {
      return true;
    }
    if (!this.definedParameters.hasOwnProperty(inputParameter)) {
      return false;
    }
  }

  checkHowToBuildIcon(): void {
    /*for index icon*/
    if (this.checkIfIconIsDefined() && this.checkTypeOfIcon() === 'index') {
      this.createIndexIcon();
    }
    /*for normal icon*/
    if (this.checkIfIconIsDefined() && this.checkTypeOfIcon() === 'icon') {
      if (
        this.checkWhatIconOfInputParameterConsistsOf() ===
        'mainIcon-additionalIcon-text'
      ) {
        this.createIconWith3Components();
      }
      if (
        this.checkWhatIconOfInputParameterConsistsOf() ===
        'mainIcon-additionalIcon'
      ) {
        this.createIconWith2Components('mainIcon-additionalIcon');
      }
      if (this.checkWhatIconOfInputParameterConsistsOf() === 'mainIcon-text') {
        this.createIconWith2Components('mainIcon-text');
      }
      if (this.checkWhatIconOfInputParameterConsistsOf() === 'mainIcon') {
        this.createIconWith1Components('singleMainIcon');
      }
    }
    if (!this.checkIfIconIsDefined()) {
      this.createIconWith1Components('notDefinedIcon');
    }
  }

  /* returns the type of inputParameter (when no type defined --> it returns 'icon' as default)
   * is needed to differentiate between normal icons and other icons (e.g. index icons)*/
  checkTypeOfIcon(): string {
    const inputParameter = this.inputParameter();
    if (
      this.checkIfIconIsDefined() &&
      this.definedParameters[inputParameter].hasOwnProperty('type')
    ) {
      return this.definedParameters[inputParameter]['type'];
    }
    if (
      this.checkIfIconIsDefined() &&
      !this.definedParameters[inputParameter].hasOwnProperty('type')
    ) {
      return 'icon';
    }
  }

  /*checks what inputParameter consists of and returns a string with the parts
  e.g. 'mainIcon-AdditionalIcon-text' for icon with mainIcon, additionalIcon and text*/
  checkWhatIconOfInputParameterConsistsOf(): string {
    /* if icon consists of: mainIcon-AdditionalIcon-text*/
    const inputParameter = this.inputParameter();
    if (
      this.definedParameters[inputParameter].hasOwnProperty('mainIcon') &&
      this.definedParameters[inputParameter].hasOwnProperty('additionalIcon') &&
      this.definedParameters[inputParameter].hasOwnProperty('text')
    ) {
      return 'mainIcon-additionalIcon-text';
    }
    /* if icon consists of: mainIcon-additionalIcon*/
    if (
      this.definedParameters[inputParameter].hasOwnProperty('mainIcon') &&
      this.definedParameters[inputParameter].hasOwnProperty('additionalIcon') &&
      !this.definedParameters[inputParameter].hasOwnProperty('text')
    ) {
      return 'mainIcon-additionalIcon';
    }
    /* if icon consists of: mainIcon-text*/
    if (
      this.definedParameters[inputParameter].hasOwnProperty('mainIcon') &&
      !this.definedParameters[inputParameter].hasOwnProperty(
        'additionalIcon'
      ) &&
      this.definedParameters[inputParameter].hasOwnProperty('text')
    ) {
      return 'mainIcon-text';
    }
    /* if icon consists of: mainIcon*/
    if (
      this.definedParameters[inputParameter].hasOwnProperty('mainIcon') &&
      !this.definedParameters[inputParameter].hasOwnProperty(
        'additionalIcon'
      ) &&
      !this.definedParameters[inputParameter].hasOwnProperty('text')
    ) {
      return 'mainIcon';
    }
  }

  createIconWith3Components(): void {
    /*create elements*/
    let faLayers = this.createLayerWrapperElement();
    const mainIcon = this.createIconElement('mainIcon');
    const additionalIcon = this.createIconElement('additionalIcon');
    const text = this.createTextElement();

    /*get parent node of where directive was placed in html*/
    let parentNode = this.renderer.parentNode(this.elementRef.nativeElement);

    /*append all single parts to faLayers (= wrapper for icons)*/
    faLayers = this.appendChildElementsToParentElement(
      faLayers,
      mainIcon,
      additionalIcon,
      text
    );

    if (typeof this.transformation() !== 'undefined') {
      this.applyInputTransformation(faLayers);
    }
    /*append all together to parent node where directive was placed in html*/
    parentNode = this.appendChildElementsToParentElement(parentNode, faLayers);
  }

  createIconWith2Components(iconToCreate: string): void {
    const inputParameter = this.inputParameter();
    /*create elements*/
    let faLayers = this.createLayerWrapperElement();
    const faStyle = this.definedParameters[inputParameter].hasOwnProperty(
      'mainStyle'
    )
      ? this.definedParameters[inputParameter]['mainStyle']
      : this.fontAwesomeStyle();
    const mainIcon = this.createIconElement('mainIcon', faStyle);

    /*get parent node of where directive was placed in html*/
    let parentNode = this.renderer.parentNode(this.elementRef.nativeElement);

    /* differentiate icon:  mainIcon-additionalIcon OR mainIcon-text*/
    if (iconToCreate === 'mainIcon-additionalIcon') {
      const additionalIcon = this.createIconElement('additionalIcon');
      /*append all single parts to faLayers (= wrapper for icons)*/
      faLayers = this.appendChildElementsToParentElement(
        faLayers,
        mainIcon,
        additionalIcon
      );
    }
    if (iconToCreate === 'mainIcon-text') {
      const text = this.createTextElement();
      faLayers = this.appendChildElementsToParentElement(
        faLayers,
        mainIcon,
        text
      );
    }

    if (typeof this.transformation() !== 'undefined') {
      this.applyInputTransformation(faLayers);
    }
    /*append all together to parent node where directive was placed in html*/
    parentNode = this.appendChildElementsToParentElement(parentNode, faLayers);
  }

  createIconWith1Components(iconToCreate): any {
    /*create elements*/
    const inputParameter = this.inputParameter();
    const faStyle =
      this.definedParameters.hasOwnProperty(inputParameter) &&
      this.definedParameters[inputParameter].hasOwnProperty('mainStyle')
        ? this.definedParameters[inputParameter]['mainStyle']
        : this.fontAwesomeStyle();
    const icon = this.createIconElement(iconToCreate, faStyle);

    if (typeof this.transformation() !== 'undefined') {
      this.applyInputTransformation(icon);
    }
    /*get parent node of where directive was placed in html*/
    let parentNode = this.renderer.parentNode(this.elementRef.nativeElement);
    /*append single icon to parent node where directive was placed in html*/
    parentNode = this.appendChildElementsToParentElement(parentNode, icon);
  }

  /*functions to create needed Elements*/
  createLayerWrapperElement(): any {
    let layerWrapper = this.renderer.createElement('span');
    layerWrapper = this.addClassesToElement(layerWrapper, 'fa-fw', 'fa-layers');

    const additionalClasses = this.additionalClasses();
    if (typeof additionalClasses !== 'undefined') {
      layerWrapper = this.addClassesToElement(
        layerWrapper,
        ...additionalClasses
      );
    }
    const size = this.size();
    if (typeof size !== 'undefined') {
      layerWrapper = this.addClassesToElement(layerWrapper, size);
    }
    return layerWrapper;
  }

  createIconElement(iconToCreate: string, faStyle?: string): any {
    const fontAwesomeStyle = this.fontAwesomeStyle();
    if (!faStyle) {
      faStyle = fontAwesomeStyle;
    }

    const inputParameter = this.inputParameter();
    /* singleMainIcon when definedParameter is just mainIcon (without layer)
    --> applies additionalclasses (css-classes) right on element and not on layer */
    if (iconToCreate === 'singleMainIcon') {
      let mainIcon = this.renderer.createElement('i');
      /*add icon-class*/
      mainIcon = this.addClassesToElement(
        mainIcon,
        faStyle,
        this.definedParameters[inputParameter]['mainIcon']
      );
      const additionalClasses = this.additionalClasses();
      /*adding other classes that need to be applied on single icon*/
      if (typeof additionalClasses !== 'undefined') {
        mainIcon = this.addClassesToElement(mainIcon, ...additionalClasses);
      }
      const size = this.size();
      if (typeof size !== 'undefined') {
        mainIcon = this.addClassesToElement(mainIcon, size);
      }
      return mainIcon;
    }
    /*mainIcon (within layer) --> additional classes are applied on layer not on element*/
    if (iconToCreate === 'mainIcon') {
      let mainIcon = this.renderer.createElement('i');
      mainIcon = this.addClassesToElement(
        mainIcon,
        faStyle,
        this.definedParameters[inputParameter]['mainIcon']
      );
      if (
        this.definedParameters[inputParameter].hasOwnProperty('mainTransform')
      ) {
        this.renderer.setAttribute(
          mainIcon,
          'data-fa-transform',
          this.definedParameters[inputParameter]['mainTransform']
        );
      }
      return mainIcon;
    }
    /*additionalIcon (within layer)*/
    if (iconToCreate === 'additionalIcon') {
      let additionalIcon = this.renderer.createElement('i');
      additionalIcon = this.addClassesToElement(
        additionalIcon,
        fontAwesomeStyle,
        this.definedParameters[inputParameter]['additionalIcon']
      );
      /*applies transformation on additional icon*/
      if (
        this.definedParameters[inputParameter].hasOwnProperty(
          'additionalIcon_position'
        )
      ) {
        additionalIcon = this.transformAdditionalIcon(additionalIcon);
      }
      return additionalIcon;
    }
    /*notDefinedIcon*/
    if (iconToCreate === 'notDefinedIcon') {
      let notDefinedIcon = this.renderer.createElement('i');
      /*add icon-class*/
      notDefinedIcon = this.addClassesToElement(
        notDefinedIcon,
        fontAwesomeStyle,
        'fa-' + inputParameter
      );
      const additionalClasses = this.additionalClasses();
      /*adding other classes that need to be applied on single icon*/
      if (typeof additionalClasses !== 'undefined') {
        notDefinedIcon = this.addClassesToElement(
          notDefinedIcon,
          ...additionalClasses
        );
      }
      const size = this.size();
      if (typeof size !== 'undefined') {
        notDefinedIcon = this.addClassesToElement(notDefinedIcon, size);
      }
      return notDefinedIcon;
    }
  }

  createIndexIcon(): void {
    const fontAwesomeStyleForBrackets = 'fal';
    const transformationOfBrackets = 'grow-11';
    const transformationOfMainIconInBrackets = 'shrink-2';

    /*create elements*/
    const faLayers = this.createLayerWrapperElement();
    const mainIcon = this.createIconElement('mainIcon');

    /*get parent node of where directive was placed in html*/
    const parentNode = this.renderer.parentNode(this.elementRef.nativeElement);

    /*create brackets*/
    let additionalIcon = this.renderer.createElement('i');
    additionalIcon = this.addClassesToElement(
      additionalIcon,
      fontAwesomeStyleForBrackets,
      this.definedParameters[this.inputParameter()]['brackets']
    );

    /*transforms brackets*/
    this.renderer.setAttribute(
      additionalIcon,
      'data-fa-transform',
      transformationOfBrackets
    );
    /*transforms mainIcon*/
    this.renderer.setAttribute(
      mainIcon,
      'data-fa-transform',
      transformationOfMainIconInBrackets
    );

    /*append all single parts to faLayers (= wrapper for icons)*/
    this.appendChildElementsToParentElement(faLayers, mainIcon, additionalIcon);
    /*appends faLayers which contains all other elements to parent node where wm-icon directive was placed in html code*/
    this.appendChildElementsToParentElement(parentNode, faLayers);
  }

  createTextElement(): any {
    /*create Text-Element*/
    let textElement = this.renderer.createElement('span');
    textElement = this.addClassesToElement(textElement, 'fa-layers-text');
    /*get text that should be added & create text-element*/
    const inputParameter = this.inputParameter();
    const textToAddToElement = this.definedParameters[inputParameter]['text'];
    const createdText = this.renderer.createText(textToAddToElement);
    /*append text to text-element*/
    this.renderer.appendChild(textElement, createdText);

    if (
      this.definedParameters[inputParameter].hasOwnProperty('text_position')
    ) {
      this.transformTextElement(textElement);
    }
    return textElement;
  }

  transformTextElement(textElement): void {
    const transformation =
      this.definedParameters[this.inputParameter()]['text_position'];
    this.renderer.setAttribute(
      textElement,
      'data-fa-transform',
      transformation
    );
  }

  transformAdditionalIcon(additionalIconToBeTransformed): any {
    const transformation =
      this.definedParameters[this.inputParameter()]['additionalIcon_position'];
    this.renderer.setAttribute(
      additionalIconToBeTransformed,
      'data-fa-transform',
      transformation
    );

    return additionalIconToBeTransformed;
  }

  applyInputTransformation(iconThatShouldBeTransformed): void {
    this.renderer.setAttribute(
      iconThatShouldBeTransformed,
      'data-fa-transform',
      this.transformation()
    );
  }

  addClassesToElement(elementToAddClassTo, ...classes): any {
    classes.map(classToAdd =>
      this.renderer.addClass(elementToAddClassTo, classToAdd)
    );
    return elementToAddClassTo;
  }

  appendChildElementsToParentElement(parentElement, ...childelement): any {
    childelement.map(childElementsToAdd =>
      this.renderer.appendChild(parentElement, childElementsToAdd)
    );
    return parentElement;
  }

  /*SPECIAL PARAMETER*/
  checkForSpecialParameter(): boolean {
    const inputParameter = this.inputParameter();
    if (inputParameter.substr(0, 2) === 'rr') {
      this.inputParameter.set('rr');
      return true;
    } else {
      return false;
    }
  }
}
